import React, { useState } from 'react';
import { EnvelopeIcon, ChatBubbleLeftRightIcon, SparklesIcon } from '@heroicons/react/24/outline';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'Competition Issue',
    message: ''
  });

  const supportEmail = "teamgenzevaluation@gmail.com";

  const isEmailValid = /\S+@\S+\.\S+/.test(formData.email);
  const isFormComplete =
    formData.name.trim() !== '' &&
    isEmailValid &&
    formData.message.trim().length > 10;

  const mailtoLink = `mailto:${supportEmail}?subject=${encodeURIComponent(
    `${formData.subject} - From ${formData.name}`
  )}&body=${encodeURIComponent(
    `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
  )}`;

  return (
    <div className="min-h-screen bg-[#050505] pt-32 pb-24 px-6 selection:bg-indigo-500/30">
      <div className="max-w-6xl mx-auto">

        {/* HEADER */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 bg-indigo-500/10 border border-indigo-500/20 px-4 py-1.5 rounded-full mb-8">
            <SparklesIcon className="h-4 w-4 text-indigo-400" />
            <span className="text-indigo-400 font-semibold text-xs uppercase tracking-wider">
              Support Center
            </span>
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
            Contact Our Team
          </h1>

          <p className="text-gray-400 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            Have a question about competitions, rewards, or internships?
            Our team typically responds within 4–6 business hours.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-10">

          {/* LEFT INFO */}
          <div className="lg:col-span-1 space-y-6">

            <ContactCard
              icon={<EnvelopeIcon className="h-6 w-6" />}
              title="Email Support"
              desc={supportEmail}
            />

            <ContactCard
              icon={<ChatBubbleLeftRightIcon className="h-6 w-6" />}
              title="Response Time"
              desc="Average reply time: 4–6 business hours."
            />

            <div className="p-6 bg-indigo-500/5 border border-indigo-500/20 rounded-2xl">
              <p className="text-xs font-semibold text-indigo-400 uppercase tracking-wider mb-2">
                Helpful Tip
              </p>
              <p className="text-sm text-gray-400 leading-relaxed">
                Include your Team ID or Competition Code for faster resolution.
              </p>
            </div>
          </div>

          {/* FORM */}
          <div className="lg:col-span-2">
            <div className="bg-[#0a0a0a] border border-white/10 p-10 rounded-3xl space-y-8 shadow-xl">

              <div className="grid md:grid-cols-2 gap-8">
                <FormField
                  label="Full Name"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(val) => setFormData({ ...formData, name: val })}
                />

                <FormField
                  label="Email Address"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(val) => setFormData({ ...formData, email: val })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Subject
                </label>
                <select
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-indigo-500 focus:outline-none transition"
                  value={formData.subject}
                  onChange={(e) =>
                    setFormData({ ...formData, subject: e.target.value })
                  }
                >
                  <option className="bg-[#0a0a0a]">Competition Issue</option>
                  <option className="bg-[#0a0a0a]">Payment / Stipend Inquiry</option>
                  <option className="bg-[#0a0a0a]">Internship Question</option>
                  <option className="bg-[#0a0a0a]">General Inquiry</option>
                </select>
              </div>

              <div>
                <label className="flex justify-between text-sm font-medium text-gray-400 mb-2">
                  Message
                  <span className="text-xs text-gray-600">
                    Minimum 10 characters
                  </span>
                </label>
                <textarea
                  rows="5"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-indigo-500 focus:outline-none transition resize-none"
                  placeholder="Describe your issue or question clearly..."
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                />
              </div>

              {/* BUTTON */}
              <div className="pt-4">
                {isFormComplete ? (
                  <a
                    href={mailtoLink}
                    className="block w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-xl text-center transition shadow-lg"
                  >
                    Send Message
                  </a>
                ) : (
                  <button
                    disabled
                    className="w-full py-4 bg-white/5 text-gray-600 rounded-xl cursor-not-allowed border border-white/10"
                  >
                    Complete all fields to continue
                  </button>
                )}
              </div>

              <p className="text-center text-xs text-gray-600">
                Your information is handled securely and will never be shared.
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

const FormField = ({ label, placeholder, value, onChange, type = "text" }) => (
  <div>
    <label className="block text-sm font-medium text-gray-400 mb-2">
      {label}
    </label>
    <input
      type={type}
      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-indigo-500 focus:outline-none transition"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const ContactCard = ({ icon, title, desc }) => (
  <div className="bg-[#0a0a0a] border border-white/10 p-6 rounded-2xl hover:border-indigo-500/30 transition">
    <div className="w-10 h-10 bg-indigo-500/10 text-indigo-500 flex items-center justify-center rounded-lg mb-4">
      {icon}
    </div>
    <h3 className="text-white font-semibold text-lg mb-1">
      {title}
    </h3>
    <p className="text-gray-400 text-sm break-all">
      {desc}
    </p>
  </div>
);

export default Contact;